import React, { useState } from 'react';

interface ForexData {
    base_currency: string;
    data: {
        privacy: string;
        quotes: {
            [key: string]: number;
        };
        source: string;
        success: boolean;
        terms: string;
        timestamp: number;
    };
    status: string;
    target_currency: string;
}

const CurrencyConverter = () => {
    const [amount, setAmount] = useState<string>('');
    const [fromCurrency, setFromCurrency] = useState<string>('USD');
    const [toCurrency, setToCurrency] = useState<string>('INR');
    const [convertedAmount, setConvertedAmount] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [exchangeRate, setExchangeRate] = useState<number>(0);

    const currencies = [
        { code: 'USD', name: 'US Dollar' },
        { code: 'INR', name: 'Indian Rupee' },
        { code: 'EUR', name: 'Euro' },
        { code: 'GBP', name: 'British Pound' },
        { code: 'JPY', name: 'Japanese Yen' },
        { code: 'AUD', name: 'Australian Dollar' },
        { code: 'CAD', name: 'Canadian Dollar' },
        { code: 'CHF', name: 'Swiss Franc' },
        { code: 'CNY', name: 'Chinese Yuan' },
        { code: 'SGD', name: 'Singapore Dollar' }
    ];

    const convertCurrency = async () => {
        if (!amount || isNaN(Number(amount))) {
            setError('Please enter a valid amount');
            return;
        }

        if (fromCurrency === toCurrency) {
            setConvertedAmount(Number(amount));
            setExchangeRate(1);
            setError(null);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // Method 1: Try direct API call (might work if CORS is configured)
            const directApiUrl = `https://buyex-api-production.up.railway.app/forexrates?base_currency=${fromCurrency}&target_currency=${toCurrency}`;

            try {
                const directResponse = await fetch(directApiUrl, {
                    method: 'GET',
                    headers: {
                        'accept': 'application/json',
                        'buyex-key': process.env.BUYEX_API_KEY || '',
                        'Content-Type': 'application/json'
                    }
                });



                if (directResponse.ok) {
                    const data: ForexData = await directResponse.json();

                    if (data.status === 'success' && data.data.success) {
                        const rateKey = `${toCurrency}${fromCurrency}`;
                        const rate = data.data.quotes[rateKey];

                        if (rate) {
                            const result = Number(amount) / rate;
                            setConvertedAmount(result);
                            setExchangeRate(1 / rate);
                            setIsLoading(false);
                            return;
                        }
                    }
                }
            } catch (directError) {
                console.warn('Direct API call failed:', directError);
            }

            // Method 2: Try with CORS proxy that supports custom headers
            const corsProxyUrl = 'https://api.allorigins.win/get?url=';
            const encodedUrl = encodeURIComponent(directApiUrl);

            const proxyResponse = await fetch(corsProxyUrl + encodedUrl);

            if (proxyResponse.ok) {
                const proxyData = await proxyResponse.json();
                const data: ForexData = JSON.parse(proxyData.contents);

                if (data.status === 'success' && data.data.success) {
                    const rateKey = `${toCurrency}${fromCurrency}`;
                    const rate = data.data.quotes[rateKey];

                    if (rate) {
                        const result = Number(amount) / rate;
                        setConvertedAmount(result);
                        setExchangeRate(1 / rate);
                        setIsLoading(false);
                        return;
                    }
                }
            }

            throw new Error('Unable to fetch exchange rate from Buyex API');

        } catch (err) {
            console.error('Conversion error:', err);

            // Fallback: Use free exchange rate API as backup
            try {
                console.log('Falling back to free exchange rate API...');
                const fallbackUrl = `https://api.exchangerate-api.com/v4/latest/${fromCurrency}`;

                const fallbackResponse = await fetch(fallbackUrl);

                console.log("fallbackResponse>>>>>>>>", fallbackResponse);


                if (fallbackResponse.ok) {
                    const fallbackData = await fallbackResponse.json();
                    console.log('Fallback API response data:', fallbackData);

                    if (fallbackData.rates && fallbackData.rates[toCurrency]) {
                        const rate = fallbackData.rates[toCurrency];
                        const result = Number(amount) * rate;
                        setConvertedAmount(result);
                        setExchangeRate(rate);
                        setIsLoading(false);
                        return;
                    }
                }
            } catch (fallbackError) {
                console.error('Fallback API also failed:', fallbackError);
            }

            setError(
                err instanceof Error
                    ? `Failed to fetch exchange rate: ${err.message}`
                    : 'Failed to fetch exchange rate'
            );
        } finally {
            setIsLoading(false);
        }
    };




    const formatCurrency = (value: number, currency: string): string => {
        try {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 6,
            }).format(value);
        } catch {
            return `${currency} ${value.toFixed(2)}`;
        }
    };

    const swapCurrencies = () => {
        const temp = fromCurrency;
        setFromCurrency(toCurrency);
        setToCurrency(temp);
        setConvertedAmount(0);
        setExchangeRate(0);
        setError(null);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex">
            <main className="flex-1 p-6">
                <div className="flex justify-between items-center mb-6">
                    {/* <div>
                        <h1 className="text-3xl font-bold text-gray-800">Currency Converter</h1>
                        <p className="text-sm text-gray-600 mt-1">Buyex API • Real-time exchange rates</p>
                    </div> */}
                </div>

                <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-2xl mx-auto">
                    <h2 className="text-xl font-bold text-gray-700 mb-6 text-center">Convert Currency</h2>

                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Amount</label>
                            <input
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="Enter amount to convert"
                                className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                step="any"
                                min="0"
                            />
                        </div>

                        <div className="flex gap-4 items-end">
                            <div className="flex-1">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">From</label>
                                <select
                                    value={fromCurrency}
                                    onChange={(e) => setFromCurrency(e.target.value)}
                                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                >
                                    {currencies.map((currency) => (
                                        <option key={`from-${currency.code}`} value={currency.code}>
                                            {currency.code} - {currency.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <button
                                type="button"
                                onClick={swapCurrencies}
                                className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-4 py-3 rounded-xl transition-all duration-200 font-bold text-xl"
                                title="Swap currencies"
                            >
                                ⇄
                            </button>

                            <div className="flex-1">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">To</label>
                                <select
                                    value={toCurrency}
                                    onChange={(e) => setToCurrency(e.target.value)}
                                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                >
                                    {currencies.map((currency) => (
                                        <option key={`to-${currency.code}`} value={currency.code}>
                                            {currency.code} - {currency.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={convertCurrency}
                            disabled={isLoading || !amount}
                            className={`w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-bold text-lg shadow-lg ${isLoading || !amount ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-xl transform hover:-translate-y-0.5'
                                }`}
                        >
                            {isLoading ? (
                                <div className="flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                    Converting...
                                </div>
                            ) : (
                                'Convert Currency'
                            )}
                        </button>

                        {/* Results Section */}
                        <div className="text-center mt-6">
                            {error ? (
                                <div className={`border rounded-xl p-4 ${error.includes('fallback')
                                        ? 'bg-yellow-50 border-yellow-200'
                                        : 'bg-red-50 border-red-200'
                                    }`}>
                                    <div className={`font-semibold ${error.includes('fallback')
                                            ? 'text-yellow-700'
                                            : 'text-red-600'
                                        }`}>
                                        {error.includes('fallback') ? '⚠️ Warning' : '❌ Error'}
                                    </div>
                                    <div className={`text-sm mt-1 ${error.includes('fallback')
                                            ? 'text-yellow-600'
                                            : 'text-red-500'
                                        }`}>
                                        {error}
                                    </div>
                                </div>
                            ) : null}

                            {convertedAmount > 0 ? (
                                <div className="bg-green-50 border border-green-200 rounded-xl p-6 mt-4">
                                    <div className="text-gray-600 text-sm mb-2">
                                        {formatCurrency(Number(amount), fromCurrency)}
                                    </div>
                                    <div className="text-2xl font-bold text-green-600 mb-2">
                                        {formatCurrency(convertedAmount, toCurrency)}
                                    </div>
                                    {exchangeRate > 0 && (
                                        <div className="text-xs text-gray-500">
                                            Rate: 1 {fromCurrency} = {exchangeRate.toFixed(6)} {toCurrency}
                                        </div>
                                    )}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default CurrencyConverter;